// ─────────────────────────────────────────────────────────────────────
// CUSTOM CHOICES IN THE BOOKING SETTINGS — the MENU, not the rule.
//
// The three pickers in BookingConfigEditor (smallest gap, allowed durations,
// how far ahead bookings open) offered a fixed row of chips. A club whose
// slots are 80 minutes, or who opens bookings 10 days ahead, had no way to say
// so — not because anything refused the value, but because no chip carried it.
// `resolveBookingWindowDays` has always accepted any whole number >= 1, and
// `bufferMin` / `allowedDurations` are read as plain numbers.
//
// So what is added here is DATA FOR THE MENU and nothing else. The values the
// engine reads are unchanged in name, type and meaning; an admin can now put a
// number in front of themselves that was previously unreachable. No booking
// rule moves (docs/architecture/DOMAIN_RULES.md §5 still governs the window).
//
// Built-ins are not removable. They are the shape of the product, and a club
// that deleted «60 min» would be one support call away from wondering where it
// went; a custom value is the admin's own and is theirs to remove.
// ─────────────────────────────────────────────────────────────────────

/** The bounds a custom value for one picker has to fall inside. */
export interface OptionBounds {
  min: number;
  max: number;
}

/**
 * GENEROUS ON PURPOSE. These are not domain rules — they exist so a slip (an
 * empty field, a pasted phone number, a stray zero) cannot be stored as a
 * booking setting. Narrowing them to what «sensible clubs» do would simply
 * recreate the fixed list this exists to remove.
 */
export const GAP_BOUNDS: OptionBounds = { min: 1, max: 600 };
export const DURATION_BOUNDS: OptionBounds = { min: 5, max: 600 };
export const WINDOW_DAY_BOUNDS: OptionBounds = { min: 1, max: 365 };

/** Why a typed value was not accepted. The caller turns this into a sentence. */
export type RejectionReason = 'invalid' | 'range' | 'duplicate';

/**
 * DISCRIMINATED ON A STRING, NOT A BOOLEAN, AND THAT IS NOT A STYLE CHOICE.
 *
 * This repo compiles with `"strict": false` (tsconfig.base.json), so
 * `strictNullChecks` is off — and without it TypeScript does not narrow a
 * union by a boolean literal property. An `ok: true | false` discriminant
 * type-checks at the definition and then fails at every use, because the
 * `false` branch is never selected. A string tag narrows either way.
 */
export type AddOutcome =
  | { status: 'added'; value: number; custom: number[] }
  | { status: 'rejected'; reason: RejectionReason };

/**
 * The chips to render: built-ins, custom values AND whatever is in force, as
 * one ascending list.
 *
 * Deduplicated against the built-ins as well as itself, so a stored document
 * that already holds a duplicate (written by an older build, or by hand)
 * renders one chip rather than two identical ones that behave differently.
 *
 * `inForce` IS THE ONE THAT IS EASY TO LEAVE OUT, and leaving it out is a
 * defect rather than a smaller feature. The built-in list is not fixed for
 * ever: 45/75/105/150/240 minutes and 60/90 days were removed from it once
 * custom values existed. A club sitting on one of those keeps the SETTING —
 * nothing rewrites stored documents — so without this the chip row would go
 * quiet about a rule that is still being enforced. The admin would open the
 * dialog, see 7 days highlighted nowhere and «90 dager» absent, and have no
 * way to learn what the club is actually doing except by reading the calendar.
 *
 * Such a value renders but is NOT deletable (`isCustomOption` says no): it is
 * not the club's own chip, it is the club's current rule. Choosing something
 * else retires it, which is the only way it should disappear.
 */
export function mergedOptions(
  builtIn: readonly number[],
  custom: readonly number[] | null | undefined,
  inForce?: number | readonly number[] | null,
): number[] {
  const seen = new Set<number>(builtIn);
  const out = [...builtIn];
  const add = (v: unknown) => {
    if (typeof v !== 'number' || !Number.isInteger(v)) return;
    if (seen.has(v)) return;
    seen.add(v);
    out.push(v);
  };
  for (const v of custom ?? []) add(v);
  if (Array.isArray(inForce)) for (const v of inForce) add(v);
  else add(inForce);
  return out.sort((a, b) => a - b);
}

/** Is this value one the admin may delete — i.e. theirs rather than built in? */
export function isCustomOption(
  builtIn: readonly number[],
  custom: readonly number[] | null | undefined,
  value: number,
): boolean {
  return !builtIn.includes(value) && (custom ?? []).includes(value);
}

/**
 * Add a typed value to the custom list.
 *
 * Takes the RAW STRING from the input rather than a number, because the three
 * ways a field can be wrong — empty, not a number, `7.5` — are all the same
 * answer to the admin and should not each need a guard at the call site.
 * `Number('')` is 0, which is why the emptiness check comes first.
 */
export function addCustomOption(
  builtIn: readonly number[],
  custom: readonly number[] | null | undefined,
  raw: string,
  bounds: OptionBounds,
): AddOutcome {
  const trimmed = raw.trim();
  if (trimmed === '') return { status: 'rejected', reason: 'invalid' };
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return { status: 'rejected', reason: 'invalid' };

  const current = custom ?? [];
  // DUPLICATE IS CHECKED BEFORE RANGE, and the gap picker is why. `0` is a
  // built-in there — the «off» chip — while the bounds start at 1, because 0
  // is not a value anyone may re-add. Checking range first would answer «must
  // be between 1 and 600» and send the admin looking for a number, when the
  // truthful answer is that the chip they want is already on screen.
  //
  // Saying so also beats silently doing nothing, which reads as a dead button.
  if (builtIn.includes(n) || current.includes(n))
    return { status: 'rejected', reason: 'duplicate' };

  if (n < bounds.min || n > bounds.max) return { status: 'rejected', reason: 'range' };

  return { status: 'added', value: n, custom: [...current, n].sort((a, b) => a - b) };
}

/** Remove a custom value. Built-ins and unknown values are left alone. */
export function removeCustomOption(
  custom: readonly number[] | null | undefined,
  value: number,
): number[] {
  return (custom ?? []).filter((v) => v !== value);
}

/**
 * What a MULTI-SELECT picker's selection becomes when a custom option goes.
 *
 * Deleting the chip must deselect it too. A value left in `allowedDurations`
 * with no chip to show it is an active booking rule nobody can see — the
 * customer gets offered 80 minutes, the admin looks at the dialog and finds
 * nothing that says so. Invisible state is worse than a lost setting.
 */
export function selectionAfterRemoval(selection: readonly number[], value: number): number[] {
  return selection.filter((v) => v !== value);
}

/**
 * What a SINGLE-SELECT picker's value becomes when the selected option goes.
 *
 * Falls back to `fallback` — the built-in default — rather than to whatever
 * happens to be first, and rather than leaving the deleted number in place.
 * The change is visible in the dialog (the highlight moves, the summary line
 * updates) and nothing is stored until Save, so the admin can still cancel.
 */
export function valueAfterRemoval(current: number, removed: number, fallback: number): number {
  return current === removed ? fallback : current;
}
